import React, { useRef } from 'react'
import { LargeDialogShell } from '@/components/admin/atoms/layout/LargeDialogShell'
import { Stack } from '@/components/admin/atoms/layout/Stack'
import { Inline } from '@/components/admin/atoms/layout/Inline'
import { MediaGrid } from '@/components/admin/atoms/layout/MediaGrid'
import { MediaThumbnail } from '@/components/admin/atoms/media/MediaThumbnail'
import { Heading } from '@/components/admin/atoms/typography/Heading'
import { Text } from '@/components/admin/atoms/typography/Text'
import { TextInput } from '@/components/admin/atoms/inputs/TextInput'
import { ActionButton } from '@/components/admin/atoms/actions/ActionButton'
import { Spinner } from '@/components/admin/atoms/feedback/Spinner'
import { useMediaPickerDialogVM } from './MediaPickerDialog.vm'

interface MediaPickerDialogProps {
  open: boolean
  onSelect: (url: string, blobKey: string) => void
  onClose: () => void
  className?: never
}

export const MediaPickerDialog: React.FC<MediaPickerDialogProps> = ({
  open, onSelect, onClose,
}) => {
  const vm = useMediaPickerDialogVM({ open, onSelect, onClose })
  const fileRef = useRef<HTMLInputElement>(null)

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      vm.handleUpload(file)
      e.target.value = ''
    }
  }

  return (
    <LargeDialogShell open={open} onClose={onClose}>
      <Stack gap="4">
        <Inline justify="between" align="center">
          <Heading level={3}>Select Image</Heading>
          <ActionButton variant="outline" size="sm" onClick={() => fileRef.current?.click()} loading={vm.uploading}>
            Upload New
          </ActionButton>
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            onChange={handleFileChange}
            style={{ display: 'none' }}
          />
        </Inline>

        <TextInput
          value={vm.search}
          onChange={vm.setSearch}
          placeholder="Search images…"
          type="search"
        />

        {vm.loading ? (
          <Inline justify="center">
            <Spinner size="lg" />
          </Inline>
        ) : vm.mediaItems.length === 0 ? (
          <Text size="sm" color="muted">No images found. Upload one to get started.</Text>
        ) : (
          <MediaGrid>
            {vm.mediaItems.map((item) => (
              <MediaThumbnail
                key={item.id}
                url={item.url}
                name={item.name}
                filename={item.filename}
                isImage={item.is_image}
                selected={vm.selectedId === item.id}
                onClick={() => vm.setSelectedId(item.id)}
              />
            ))}
          </MediaGrid>
        )}

        <Inline justify="end" gap="2">
          <ActionButton variant="ghost" onClick={onClose}>
            Cancel
          </ActionButton>
          <ActionButton
            variant="primary"
            onClick={vm.handleConfirm}
            disabled={!vm.selectedItem}
          >
            Select Image
          </ActionButton>
        </Inline>
      </Stack>
    </LargeDialogShell>
  )
}
